<script setup lang="ts">
  /**
   * PDFViewer Component
   * A reusable component for displaying PDF documents in a modal dialog.
   *
   * Features:
   * - Modal PDF viewer with backdrop click to close
   * - Page navigation
   * - Text highlighting
   * - Loading state
   * - Responsive design
   *
   * @example
   * ```vue
   *   <PDFViewer
   *     v-model:isOpen="isOpen"
   *     v-model:url="pdfUrl"
   *     :filename="filename"
   *     :highlight-text="'text to highlight'"
   *     :initial-page="1"
   *     @loaded="(pdfInstance) => console.log('PDF loaded', pdfInstance)"
   *     @page-change="(page) => console.log('Page changed', page)"
   *   />
   * ```
   *
   * @typedef {Object} Props
   * @property {string} url - URL of the PDF file to display
   * @property {string} filename - Name of the file to display in the header
   * @property {boolean} isOpen - Controls visibility of the modal
   * @property {number} [initialPage=1] - Initial page number to display
   * @property {string} [highlightText] - Text to highlight in the PDF
   * @property {number} [scale=1.1] - Scale factor for the PDF display
   *
   * @typedef {Object} Emits
   * @property {(event: 'update:isOpen', value: boolean) => void} update:isOpen - Emitted when modal visibility changes
   * @property {(event: 'update:url', value: string) => void} update:url - Emitted when URL should be updated
   * @property {(event: 'loaded', pdfInstance: any) => void} loaded - Emitted when PDF is loaded
   * @property {(event: 'pageChange', page: number) => void} pageChange - Emitted when page changes
   * @property {(event: 'highlight', event: any) => void} highlight - Emitted on text highlight
   */

  import { ref, computed, watch } from 'vue'
  import { usePDF, VuePDF } from '@tato30/vue-pdf'
  import PDFNavigation from '@/components/pdf/genericPdfNav.vue'

  interface Props {
    url: string
    filename: string
    isOpen: boolean
    initialPage?: number
    highlightText?: string
    scale?: number
  }

  const props = defineProps<Props>()

  const emit = defineEmits<{
    (e: 'update:isOpen', value: boolean): void
    (e: 'update:url', value: string): void
    (e: 'loaded', pdfInstance: any): void
    (e: 'pageChange', page: number): void
    (e: 'highlight', event: any): void
  }>()

  const currentPage = ref(props.initialPage || 1)
  const isLoading = ref(true)
  const internalUrl = ref(props.url)

  const highlightOptions = computed(() => ({
    ignoreCase: true,
    completeWords: true
  }))

  // Watch for URL changes from parent
  watch(
    () => props.url,
    (newUrl) => {
      internalUrl.value = newUrl
    }
  )

  // Use internal URL for PDF loading
  const { pdf, pages } = usePDF(internalUrl)

  function closeViewer() {
    emit('update:isOpen', false)
    emit('update:url', '')
    internalUrl.value = ''
    currentPage.value = props.initialPage || 1
    isLoading.value = true
  }

  function onPdfLoaded(pdfInstance: any) {
    isLoading.value = false
    emit('loaded', pdfInstance)
  }

  function updateCurrentPage(newPage: number) {
    currentPage.value = newPage
    emit('pageChange', newPage)
  }

  function onHighlight(event: any) {
    emit('highlight', event)
  }

  function handleBackdropClick(event: MouseEvent) {
    // Only close if clicking directly on the backdrop
    if (event.target === event.currentTarget) {
      closeViewer()
    }
  }
</script>

<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-[60] flex items-center justify-center bg-gray-900/50 backdrop-blur-sm"
    @click="handleBackdropClick"
  >
    <div class="flex h-[95vh] w-[45vw] flex-col overflow-hidden rounded-lg bg-white shadow-lg">
      <!-- Header -->
      <div
        class="flex h-14 shrink-0 items-center justify-between border-b border-gray-200 bg-gray-50 px-6"
      >
        <h2 class="truncate text-base font-medium text-gray-900">{{ filename }}</h2>
        <button
          type="button"
          class="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-400 transition
            hover:bg-gray-100 hover:text-gray-500"
          @click="closeViewer"
        >
          <span class="sr-only">Close</span>
          <svg
            class="h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z"
            />
          </svg>
        </button>
      </div>

      <!-- Content -->
      <div class="relative flex-1 overflow-hidden bg-white">
        <div
          v-if="isLoading"
          class="absolute inset-0 z-10 flex items-center justify-center bg-white/90"
        >
          <div class="flex items-center gap-3">
            <div
              class="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"
            ></div>
            <span class="text-sm font-medium text-gray-600">Loading document...</span>
          </div>
        </div>
        <div class="flex h-full w-full items-center justify-center overflow-auto">
          <div class="w-full max-w-3xl px-6">
            <VuePDF
              :pdf="pdf"
              :page="currentPage"
              :scale="props.scale ?? 1.1"
              :highlight-text="highlightText"
              :highlight-options="highlightOptions"
              text-layer
              class="mx-auto"
              @loaded="onPdfLoaded"
              @highlight="onHighlight"
            />
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="flex h-14 shrink-0 items-center justify-center border-t border-gray-200 bg-white px-6"
      >
        <PDFNavigation
          :current-page="currentPage"
          :total-pages="pages"
          :show-prev-next-buttons="true"
          :show-page-buttons="true"
          @update-page="updateCurrentPage"
        />
      </div>
    </div>
  </div>
</template>
